import { capture } from '@parcel/analytics/browser';
import type {
  DynamicViewDefinition,
  StaticViewDefinition,
  TableFilter,
  WorkspaceViewDataset,
  WorkspaceViewPresentation,
} from '@parcel/filters';
import { toast } from '@parcel/ui';
import { useQueryClient } from '@tanstack/react-query';
import type { Table } from '@tanstack/react-table';
import * as React from 'react';
import { apiErrorCode } from './api.js';
import { EMPTY_TABLE_FILTER, definitionToTableFilter, tableFiltersEqual } from './table-filter.js';
import {
  type ViewUpsertInput,
  type WorkspaceView,
  applyWorkspacePresentation,
  serializeWorkspacePresentation,
  serializeWorkspaceView,
  useWorkspaceViewMutations,
  useWorkspaceViews,
  workspaceViewKeys,
} from './workspace-views.js';

export const UNSAVEABLE_FILTERS_MESSAGE =
  'These filters cannot be saved as a View. Check that every filter has a value.';

/**
 * Serialise the current filters, or report why not and return null.
 *
 * The saved-View schema checks every rule against the field list; a chip left without a
 * value fails it. The throw used to escape an async callback every caller invokes with
 * `void`, which made Save silently do nothing.
 */
function serializeViewOrReport<TData>(
  dataset: WorkspaceViewDataset,
  table: Table<TData>,
  filterState: TableFilter,
): ReturnType<typeof serializeWorkspaceView<TData>> | null {
  try {
    return serializeWorkspaceView(dataset, table, filterState);
  } catch {
    toast.error(UNSAVEABLE_FILTERS_MESSAGE);
    return null;
  }
}

/**
 * Canonical form for equality. Two things make raw JSON.stringify wrong here:
 * key order (jsonb does not preserve it, and toggling a column appends its key),
 * and `true` entries (TanStack treats a missing column as visible, so `{}` and
 * `{stage: true}` are the same view). Sort order stays significant.
 */
function canonicalPresentation(presentation: WorkspaceViewPresentation): string {
  const hidden = Object.keys(presentation.column_visibility)
    .filter((column) => presentation.column_visibility[column] === false)
    .sort();
  return JSON.stringify({
    version: presentation.version,
    sorting: presentation.sorting.map(({ id, desc }) => [id, desc]),
    hidden,
  });
}

export function presentationsEqual(
  a: WorkspaceViewPresentation,
  b: WorkspaceViewPresentation,
): boolean {
  return canonicalPresentation(a) === canonicalPresentation(b);
}

export type ResolveActiveWorkspaceViewResult =
  | { status: 'none' }
  | { status: 'loading' }
  /**
   * The Views list failed to load, so whether this View exists is unknowable. Distinct
   * from `missing`, which is a fact: the list arrived and this id was not in it. Only
   * `missing` justifies discarding the id.
   */
  | { status: 'unavailable' }
  | { status: 'missing'; reason: 'unknown' | 'deleted' | 'dataset-mismatch' }
  | { status: 'ok'; view: WorkspaceView };

export function resolveActiveWorkspaceView(
  views: WorkspaceView[] | undefined,
  activeViewId: string | null,
  dataset: WorkspaceViewDataset,
  listStatus: 'pending' | 'success' | 'error',
): ResolveActiveWorkspaceViewResult {
  if (!activeViewId) return { status: 'none' };
  // The list fetch failed. Do not hang as loading, and do not report it as missing: a
  // deleted View and an unreachable API look identical from here, and treating the second
  // as the first tells the user something false and throws away a still-valid deep link.
  if (listStatus === 'error') return { status: 'unavailable' };
  if (listStatus === 'pending' || views === undefined) return { status: 'loading' };
  const view = views.find((candidate) => candidate.id === activeViewId);
  if (!view) return { status: 'missing', reason: 'unknown' };
  if (view.dataset !== dataset) return { status: 'missing', reason: 'dataset-mismatch' };
  return { status: 'ok', view };
}

/**
 * Gates entity list fetches until a URL `view` can be classified as Static
 * (send view_id), Dynamic (omit view_id), or missing/error (omit view_id).
 * Without a `view` key the book loads immediately.
 */
export type EntityListViewScope =
  | { ready: false; staticViewId?: undefined }
  | { ready: true; staticViewId: string | undefined };

export type ViewHydrationState = {
  urlHasFilterKeys: boolean;
  hydratedViewId: string | null;
  filterState: TableFilter;
};

/**
 * Has the URL caught up with this Dynamic View?
 *
 * A Favorites deep link carries only `?view=`; the saved filters arrive one navigation
 * later. Fetching in between sends empty filters, whose query key is identical to the
 * unscoped book's, so a warm cache paints the whole book and `keepPreviousData` pins it
 * there for the entire scoped fetch.
 *
 * Every term below reads the URL as it stands right now. Nothing may latch on *dispatching*
 * hydration: `setFilterState` is a router navigate, so the commit immediately after it still
 * carries the old, empty search. A latch set there re-opens the gate during precisely the
 * window it exists to cover.
 */
export function isDynamicViewScopeSettled(
  view: WorkspaceView,
  hydration: ViewHydrationState,
): boolean {
  // Latched by an earlier render that saw the URL represent this View. The user may since
  // have removed every filter chip, which must keep fetching rather than gate forever.
  if (hydration.hydratedViewId === view.id) return true;
  // Filters in the URL: either hydration landed, or a shared link arrived carrying its own
  // filters, which win over the saved definition (decideViewHydration's preserve-url path).
  //
  // This term reads "the URL carries filters", not "the URL carries *this View's* filters".
  // It holds only while nothing moves `?view=` without replacing the filter keys in the same
  // navigation: applyView/applyBaseView call setFilterState before setActiveViewId, and the
  // router's second functional search updater builds on the first (it reads pendingMatches,
  // which the first navigation populates synchronously), so the two compose into one commit.
  // The Favorites Link passes a `search` object literal, which replaces the search rather
  // than merging into it. A future `?view=` setter that merged instead would strand the
  // outgoing View's filters, and this gate and preserve-url would both adopt them as the
  // incoming View's own.
  if (hydration.urlHasFilterKeys) return true;
  // A View whose saved criteria are empty produces no filter keys at all, so it has to be
  // compared against rather than waited for.
  return tableFiltersEqual(
    hydration.filterState,
    definitionToTableFilter(view.definition as DynamicViewDefinition),
  );
}

export function resolveEntityListViewScope(
  views: WorkspaceView[] | undefined,
  urlViewId: string | null,
  dataset: WorkspaceViewDataset,
  listStatus: 'pending' | 'success' | 'error',
  hydration?: ViewHydrationState,
): EntityListViewScope {
  if (!urlViewId) return { ready: true, staticViewId: undefined };

  const resolved = resolveActiveWorkspaceView(views, urlViewId, dataset, listStatus);
  if (resolved.status === 'loading') return { ready: false };
  if (resolved.status === 'ok' && resolved.view.view_type === 'static') {
    return { ready: true, staticViewId: resolved.view.id };
  }
  if (hydration && resolved.status === 'ok' && resolved.view.view_type === 'dynamic') {
    if (!isDynamicViewScopeSettled(resolved.view, hydration)) return { ready: false };
  }
  // Settled Dynamic, unavailable, missing, or none — fetch the book (no Static view_id).
  return { ready: true, staticViewId: undefined };
}

/**
 * The Dynamic View whose criteria the URL currently represents, or null. This, and only
 * this, is what the latch may record.
 *
 * `ready` is a much weaker signal and must not be used here: the gate also opens for
 * Static Views, for an id the loaded list did not contain, and for a Views list that
 * failed outright. In none of those has the URL demonstrated anything about a Dynamic
 * View's filters. Latching on `ready` meant a failed list recorded the id anyway, so when
 * the list recovered the gate found a latch it had never earned and opened with empty
 * filters, which is the unscoped-book paint this gate exists to prevent.
 */
export function settledDynamicViewId(
  views: WorkspaceView[] | undefined,
  urlViewId: string | null,
  dataset: WorkspaceViewDataset,
  listStatus: 'pending' | 'success' | 'error',
  hydration: ViewHydrationState,
): string | null {
  if (!urlViewId) return null;
  const resolved = resolveActiveWorkspaceView(views, urlViewId, dataset, listStatus);
  if (resolved.status !== 'ok' || resolved.view.view_type !== 'dynamic') return null;
  return isDynamicViewScopeSettled(resolved.view, hydration) ? resolved.view.id : null;
}

/**
 * Owns the hydration latch so the entity-list gate can read it. Must be called *before*
 * the entity list query, which is why the latch cannot live inside
 * {@link useWorkspaceTableViews} (that hook needs a `table`, which needs the data).
 */
export function useEntityListViewScope(input: {
  views: WorkspaceView[] | undefined;
  urlViewId: string | null;
  dataset: WorkspaceViewDataset;
  listStatus: 'pending' | 'success' | 'error';
  urlHasFilterKeys: boolean;
  filterState: TableFilter;
}): EntityListViewScope {
  const [hydratedViewId, setHydratedViewId] = React.useState<string | null>(null);
  const hydration = {
    urlHasFilterKeys: input.urlHasFilterKeys,
    hydratedViewId,
    filterState: input.filterState,
  };
  const scope = resolveEntityListViewScope(
    input.views,
    input.urlViewId,
    input.dataset,
    input.listStatus,
    hydration,
  );

  // Latch on observation, never on dispatch. Once the URL has represented this View the
  // list stays ungated, so clearing the last filter chip does not re-gate it. The gate
  // itself owns this: a writer on the hydration side would be latching an intent, and an
  // intent is exactly what the router has not applied yet.
  //
  // A stale latch is never cleared, which is correct: it is keyed by View id, so it can
  // only ever answer for the View that earned it.
  const settledViewId = settledDynamicViewId(
    input.views,
    input.urlViewId,
    input.dataset,
    input.listStatus,
    hydration,
  );
  React.useEffect(() => {
    if (settledViewId) setHydratedViewId(settledViewId);
  }, [settledViewId]);

  return scope;
}

export type ViewHydrationIntent = 'select' | 'hydrate' | 'all';

export type ViewHydrationDecision =
  | { type: 'noop' }
  | {
      type: 'apply-dynamic';
      filterState: TableFilter;
      presentation: WorkspaceViewPresentation;
    }
  | {
      type: 'apply-static';
      filterState: TableFilter;
      presentation: WorkspaceViewPresentation;
    }
  | {
      type: 'preserve-url';
      presentation: WorkspaceViewPresentation;
    }
  | {
      type: 'apply-all';
      filterState: TableFilter;
      presentation: WorkspaceViewPresentation;
    };

export function decideViewHydration(input: {
  intent: ViewHydrationIntent;
  view: WorkspaceView | null;
  urlHasFilterKeys: boolean;
  alreadyHydratedViewId: string | null;
  basePresentation?: WorkspaceViewPresentation;
}): ViewHydrationDecision {
  if (input.intent === 'all') {
    return {
      type: 'apply-all',
      filterState: EMPTY_TABLE_FILTER,
      presentation: input.basePresentation ?? {
        version: 1,
        sorting: [],
        column_visibility: {},
      },
    };
  }

  const view = input.view;
  if (!view) return { type: 'noop' };

  if (input.intent === 'hydrate' && input.alreadyHydratedViewId === view.id) {
    return { type: 'noop' };
  }

  if (input.intent === 'hydrate' && input.urlHasFilterKeys) {
    return { type: 'preserve-url', presentation: view.presentation };
  }

  if (view.view_type === 'static') {
    return {
      type: 'apply-static',
      filterState: EMPTY_TABLE_FILTER,
      presentation: view.presentation,
    };
  }

  return {
    type: 'apply-dynamic',
    filterState: definitionToTableFilter(view.definition as DynamicViewDefinition),
    presentation: view.presentation,
  };
}

/**
 * Oldest first, so a View lands at the end of the tab row the moment it is created and
 * stays where the user last saw it.
 *
 * The list endpoint returns updated_at DESC, which is right for an API (most recently
 * touched first) and wrong for a tab bar: saving any View jumps it to the front and
 * reshuffles every tab around it, so the row the user is reading rearranges itself under
 * them. Ties break on id, which never collides, so the order is total and stable rather
 * than left to the sort's discretion.
 */
export function sortViewsByCreation(views: readonly WorkspaceView[]): WorkspaceView[] {
  const at = (view: WorkspaceView) => {
    const parsed = Date.parse(view.created_at);
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  return [...views].sort((a, b) => at(a) - at(b) || a.id.localeCompare(b.id));
}

export function isWorkspaceViewDirty(input: {
  view: WorkspaceView | null;
  filterState: TableFilter;
  presentation: WorkspaceViewPresentation;
  basePresentation: WorkspaceViewPresentation;
}): boolean {
  const { view, filterState, presentation, basePresentation } = input;
  if (!view) {
    return (
      !presentationsEqual(presentation, basePresentation) ||
      !tableFiltersEqual(filterState, EMPTY_TABLE_FILTER)
    );
  }
  if (view.view_type === 'static') {
    return !presentationsEqual(presentation, view.presentation);
  }
  return (
    !presentationsEqual(presentation, view.presentation) ||
    !tableFiltersEqual(
      filterState,
      definitionToTableFilter(view.definition as DynamicViewDefinition),
    )
  );
}

export function canSaveWorkspaceViewAsNew(input: {
  view: WorkspaceView | null;
  filterState: TableFilter;
  presentation: WorkspaceViewPresentation;
  basePresentation: WorkspaceViewPresentation;
}): boolean {
  if (input.view?.view_type === 'static') return false;
  return isWorkspaceViewDirty(input);
}

export function buildWorkspaceViewSavePayload(
  input:
    | {
        mode: 'update';
        view: WorkspaceView;
        dataset: WorkspaceViewDataset;
        definition: DynamicViewDefinition | StaticViewDefinition;
        presentation: WorkspaceViewPresentation;
      }
    | {
        mode: 'create';
        name: string;
        dataset: WorkspaceViewDataset;
        definition: DynamicViewDefinition;
        presentation: WorkspaceViewPresentation;
      },
): ViewUpsertInput {
  if (input.mode === 'create') {
    return {
      name: input.name,
      dataset: input.dataset,
      view_type: 'dynamic',
      definition: input.definition,
      presentation: input.presentation,
    };
  }

  if (input.view.view_type === 'static') {
    return {
      id: input.view.id,
      expected_revision: input.view.revision,
      presentation: input.presentation,
    };
  }

  return {
    id: input.view.id,
    expected_revision: input.view.revision,
    definition: input.definition,
    presentation: input.presentation,
  };
}

export interface UseWorkspaceTableViewsInput<TData> {
  workspaceId: string | undefined;
  dataset: WorkspaceViewDataset;
  table: Table<TData>;
  basePresentation: WorkspaceViewPresentation;
  filterState: TableFilter;
  setFilterState(next: TableFilter): void;
  activeViewId: string | null;
  setActiveViewId(viewId: string | null): void;
  /** When true, the validated URL already carries filter keys for the active View. */
  urlHasFilterKeys?: boolean;
}

export function useWorkspaceTableViews<TData>({
  workspaceId,
  dataset,
  table,
  basePresentation,
  filterState,
  setFilterState,
  activeViewId,
  setActiveViewId,
  urlHasFilterKeys = false,
}: UseWorkspaceTableViewsInput<TData>) {
  const queryClient = useQueryClient();
  const viewsQuery = useWorkspaceViews(workspaceId, dataset);
  const mutations = useWorkspaceViewMutations(workspaceId, dataset);
  const missingToastRef = React.useRef<string | null>(null);
  const unavailableToastRef = React.useRef<string | null>(null);

  // A ref, not state: this records that hydration has been *dispatched*, so a mid-flight
  // View switch cannot re-trigger the effect and re-apply the outgoing View. The list gate
  // deliberately does not read it — see isDynamicViewScopeSettled for why an intent is the
  // wrong thing to gate a fetch on.
  const hydratedViewIdRef = React.useRef<string | null>(null);
  const setHydratedViewId = React.useCallback((viewId: string | null) => {
    hydratedViewIdRef.current = viewId;
  }, []);

  const resolved = resolveActiveWorkspaceView(
    viewsQuery.data,
    activeViewId,
    dataset,
    viewsQuery.isPending ? 'pending' : viewsQuery.isError ? 'error' : 'success',
  );

  const activeView = resolved.status === 'ok' ? resolved.view : null;
  const currentPresentation = serializeWorkspacePresentation(table);

  const isDirty = isWorkspaceViewDirty({
    view: activeView,
    filterState,
    presentation: currentPresentation,
    basePresentation,
  });
  const canSaveAsNew = canSaveWorkspaceViewAsNew({
    view: activeView,
    filterState,
    presentation: currentPresentation,
    basePresentation,
  });

  const applyDecision = React.useCallback(
    (decision: ViewHydrationDecision, nextViewId: string | null) => {
      if (decision.type === 'noop') return;
      // A selection belongs to the View it was made in. Rows are keyed by record id, so
      // carrying it across a switch resurfaces rows picked in another context the moment
      // the user returns, and the selection bar will happily bulk-write them.
      table.resetRowSelection();
      if (decision.type === 'preserve-url') {
        applyWorkspacePresentation(table, decision.presentation);
        setHydratedViewId(nextViewId);
        return;
      }
      applyWorkspacePresentation(table, decision.presentation);
      setFilterState(decision.filterState);
      setHydratedViewId(nextViewId);
    },
    [setFilterState, setHydratedViewId, table],
  );

  const resolvedStatus = resolved.status;
  const resolvedReason = resolved.status === 'missing' ? resolved.reason : null;
  const resolvedView = resolved.status === 'ok' ? resolved.view : null;

  React.useEffect(() => {
    if (resolvedStatus === 'loading') return;

    if (resolvedStatus === 'unavailable') {
      // The Views list failed, so this id cannot be classified. Show the unscoped book
      // rather than hanging, but leave `?view=` in the URL and leave the user's filters
      // and presentation alone: the request retries on reconnect and window focus, and
      // clearing the id here would delete the very thing that recovery needs.
      if (activeViewId && unavailableToastRef.current !== activeViewId) {
        unavailableToastRef.current = activeViewId;
        toast.error('Could not load your Views. Showing all records.');
      }
      return;
    }
    unavailableToastRef.current = null;

    if (resolvedStatus === 'missing') {
      if (activeViewId && missingToastRef.current !== activeViewId) {
        missingToastRef.current = activeViewId;
        toast.error(
          resolvedReason === 'dataset-mismatch'
            ? 'This View belongs to a different dataset.'
            : 'This View was deleted',
        );
      }
      const decision = decideViewHydration({
        intent: 'all',
        view: null,
        urlHasFilterKeys: false,
        alreadyHydratedViewId: hydratedViewIdRef.current,
        basePresentation,
      });
      applyDecision(decision, null);
      setActiveViewId(null);
      return;
    }

    missingToastRef.current = null;

    if (resolvedStatus === 'none') {
      // Browser back (or any navigation) that drops `view` must reset presentation
      // like All — not only clear the hydration ref.
      if (hydratedViewIdRef.current != null) {
        applyWorkspacePresentation(table, basePresentation);
        table.resetRowSelection();
        setHydratedViewId(null);
      }
      return;
    }

    if (!resolvedView) return;

    const decision = decideViewHydration({
      intent: 'hydrate',
      view: resolvedView,
      urlHasFilterKeys,
      alreadyHydratedViewId: hydratedViewIdRef.current,
    });
    applyDecision(decision, resolvedView.id);
  }, [
    activeViewId,
    applyDecision,
    basePresentation,
    resolvedReason,
    resolvedStatus,
    resolvedView,
    setActiveViewId,
    setHydratedViewId,
    table,
    urlHasFilterKeys,
  ]);

  const applyBaseView = React.useCallback(() => {
    const decision = decideViewHydration({
      intent: 'all',
      view: null,
      urlHasFilterKeys: false,
      alreadyHydratedViewId: hydratedViewIdRef.current,
      basePresentation,
    });
    applyDecision(decision, null);
    setActiveViewId(null);
  }, [applyDecision, basePresentation, setActiveViewId]);

  const applyView = React.useCallback(
    (viewId: string) => {
      const view = (viewsQuery.data ?? []).find((candidate) => candidate.id === viewId);
      if (!view || view.dataset !== dataset) return;
      const decision = decideViewHydration({
        intent: 'select',
        view,
        urlHasFilterKeys: false,
        alreadyHydratedViewId: null,
      });
      applyDecision(decision, view.id);
      setActiveViewId(view.id);
    },
    [applyDecision, dataset, setActiveViewId, viewsQuery.data],
  );

  const handleConflict = React.useCallback(
    (view: WorkspaceView) => {
      if (workspaceId) {
        void queryClient.invalidateQueries({
          queryKey: workspaceViewKeys.detail(workspaceId, view.id),
        });
        void queryClient.invalidateQueries({
          queryKey: workspaceViewKeys.list(workspaceId, dataset),
        });
      }
      toast.error('This View changed since you opened it. Refresh and try again.', {
        action: {
          label: 'Refresh',
          onClick: () => {
            applyView(view.id);
          },
        },
      });
    },
    [applyView, dataset, queryClient, workspaceId],
  );

  const saveCurrent = React.useCallback(async () => {
    if (!activeView || !workspaceId) return;
    const presentation = serializeWorkspacePresentation(table);
    // A Static View has no criteria to serialise, so only the Dynamic branch can fail.
    let definition: DynamicViewDefinition | StaticViewDefinition = { version: 1 };
    if (activeView.view_type === 'dynamic') {
      const serialized = serializeViewOrReport(dataset, table, filterState);
      if (!serialized) return;
      definition = serialized.definition;
    }
    const payload = buildWorkspaceViewSavePayload({
      mode: 'update',
      view: activeView,
      dataset,
      definition,
      presentation,
    });
    try {
      await mutations.upsert.mutateAsync(payload);
    } catch (err) {
      // Match on the code, not the status. 409 also carries view_name_conflict, whose
      // own message the mutation's onError already shows; treating every 409 as a stale
      // revision produced two toasts, the second of them wrong ("refresh and try again"
      // does not help you pick a name that is not taken).
      if (apiErrorCode(err) === 'view_conflict') {
        handleConflict(activeView);
        return;
      }
      throw err;
    }
  }, [activeView, dataset, filterState, handleConflict, mutations.upsert, table, workspaceId]);

  const saveAsNew = React.useCallback(
    async (name: string) => {
      if (!workspaceId) return null;
      const serialized = serializeViewOrReport(dataset, table, filterState);
      if (!serialized) return null;
      const { definition, presentation } = serialized;
      const view = await mutations.upsert.mutateAsync(
        buildWorkspaceViewSavePayload({
          mode: 'create',
          name,
          dataset,
          definition,
          presentation,
        }),
      );
      // Dataset is a strict subset of EntityKind, so it doubles as the analytics
      // entity. This is the only create path (saveCurrent/renameView are updates),
      // so the event fires exactly once per new View, as it did on the old hook.
      capture('saved_view_created', { entity: dataset });
      setHydratedViewId(view.id);
      setActiveViewId(view.id);
      return view;
    },
    [
      dataset,
      filterState,
      mutations.upsert,
      setActiveViewId,
      setHydratedViewId,
      table,
      workspaceId,
    ],
  );

  const renameView = React.useCallback(
    async (name: string) => {
      if (!activeView || !workspaceId) return;
      try {
        await mutations.upsert.mutateAsync({
          id: activeView.id,
          expected_revision: activeView.revision,
          name,
        });
      } catch (err) {
        // Code, not status: renaming onto a taken name is the likeliest 409 there is, and
        // it arrives as view_name_conflict. The mutation's onError already shows the API's
        // own message for it; adding "refresh and try again" on top is both a second toast
        // and the wrong advice.
        if (apiErrorCode(err) === 'view_conflict') {
          handleConflict(activeView);
          return;
        }
        throw err;
      }
    },
    [activeView, handleConflict, mutations.upsert, workspaceId],
  );

  const deleteView = React.useCallback(async () => {
    if (!activeView || !workspaceId) return;
    await mutations.remove.mutateAsync(activeView.id);
    applyBaseView();
  }, [activeView, applyBaseView, mutations.remove, workspaceId]);

  const orderedViews = React.useMemo(
    () => sortViewsByCreation(viewsQuery.data ?? []),
    [viewsQuery.data],
  );

  return {
    views: orderedViews,
    activeView,
    activeViewId,
    isDirty,
    canSaveAsNew,
    isLoading: viewsQuery.isLoading,
    staticViewId: activeView?.view_type === 'static' ? activeView.id : undefined,
    applyBaseView,
    applyView,
    saveCurrent,
    saveAsNew,
    renameView,
    deleteView,
  };
}
